import React, { useMemo } from "react";
import "./EvaluationComparisonChart.css";

export function buildEvaluationComparison(questions, raters) {
  const submitted = (raters || []).filter((r) => r.status === "SUBMITTED");
  return (questions || []).map((q) => {
    const selfScores = submitted
      .filter((r) => r.raterType === "SELF")
      .flatMap((r) => r.answers || [])
      .filter((a) => a.questionId === q.id)
      .map((a) => Number(a.score));
    const otherScores = submitted
      .filter((r) => r.raterType !== "SELF")
      .flatMap((r) => r.answers || [])
      .filter((a) => a.questionId === q.id)
      .map((a) => Number(a.score));

    const selfScore =
      selfScores.length > 0
        ? Math.round(
            (selfScores.reduce((s, n) => s + n, 0) / selfScores.length) * 100,
          ) / 100
        : null;
    const othersAvg =
      otherScores.length > 0
        ? Math.round(
            (otherScores.reduce((s, n) => s + n, 0) / otherScores.length) * 100,
          ) / 100
        : null;
    const delta =
      selfScore != null && othersAvg != null
        ? Math.round((othersAvg - selfScore) * 100) / 100
        : null;

    return {
      questionId: q.id,
      text: q.text,
      maxScore: q.maxScore,
      weightPercent: Number(q.weightPercent),
      audiences: q.audiences || [],
      selfScore,
      othersAvg,
      delta,
    };
  });
}

function deltaLabel(delta, perspective) {
  if (delta == null) return null;
  if (Math.abs(delta) < 0.05) {
    return {
      text:
        perspective === "hr"
          ? "الذاتي ومتوسط الآخرين متساويان"
          : "مساوي لتقييمك",
      tone: "same",
    };
  }
  if (delta > 0) {
    return {
      text:
        perspective === "hr"
          ? "متوسط الآخرين أعلى من الذاتي"
          : "الآخرون أعلى من تقييمك",
      tone: "higher",
    };
  }
  return {
    text:
      perspective === "hr"
        ? "متوسط الآخرين أقل من الذاتي"
        : "الآخرون أقل من تقييمك",
    tone: "lower",
  };
}

function polarPoint(cx, cy, radius, angleRad) {
  return {
    x: cx + radius * Math.sin(angleRad),
    y: cy - radius * Math.cos(angleRad),
  };
}

function pointsToPolygon(points) {
  return points.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
}

/**
 * @param {{ comparison?: Array, questions?: Array, raters?: Array, perspective?: 'self'|'hr', title?: string }} props
 */
export default function EvaluationComparisonChart({
  comparison: comparisonProp,
  questions,
  raters,
  perspective = "self",
  title,
}) {
  const comparison = useMemo(() => {
    if (Array.isArray(comparisonProp) && comparisonProp.length) {
      return comparisonProp;
    }
    if (questions?.length) {
      return buildEvaluationComparison(questions, raters);
    }
    return [];
  }, [comparisonProp, questions, raters]);

  const chart = useMemo(() => {
    if (!comparison.length) return null;

    const size = 320;
    const cx = size / 2;
    const cy = size / 2;
    const maxR = 108;
    const n = comparison.length;
    const ringLevels = [0.2, 0.4, 0.6, 0.8, 1];

    const axes = comparison.map((row, i) => {
      const angle = (i / n) * Math.PI * 2;
      const maxScore = Number(row.maxScore) || 5;
      const toR = (score) =>
        score == null ? null : (Number(score) / maxScore) * maxR;
      const selfR = toR(row.selfScore);
      const othersR = toR(row.othersAvg);
      const tip = polarPoint(cx, cy, maxR, angle);
      const labelPos = polarPoint(cx, cy, maxR + 28, angle);
      return {
        ...row,
        angle,
        maxScore,
        tip,
        labelPos,
        selfPoint: selfR == null ? null : polarPoint(cx, cy, selfR, angle),
        othersPoint:
          othersR == null ? null : polarPoint(cx, cy, othersR, angle),
      };
    });

    const selfPts = axes.map((a) => a.selfPoint).filter(Boolean);
    const othersPts = axes.map((a) => a.othersPoint).filter(Boolean);

    return { size, cx, cy, maxR, ringLevels, axes, selfPts, othersPts };
  }, [comparison]);

  if (!chart) return null;

  const hasAnyScore = comparison.some(
    (row) => row.selfScore != null || row.othersAvg != null,
  );
  if (!hasAnyScore) return null;

  const selfLabel = perspective === "hr" ? "التقييم الذاتي" : "تقييمي";
  const chartTitle =
    title ||
    (perspective === "hr"
      ? "مقارنة التقييم الذاتي مع متوسط الآخرين"
      : "تقييمي مقابل متوسط الآخرين");

  return (
    <div className="eval-compare">
      <h3 className="eval-compare__title">{chartTitle}</h3>
      <p className="eval-compare__hint">
        كلما ارتفعت الدرجة ابتعدت النقطة عن المركز، وكلما انخفضت اقتربت منه
      </p>

      <div className="eval-compare__radar-wrap">
        <svg
          className="eval-compare__radar"
          viewBox={`0 0 ${chart.size} ${chart.size}`}
          role="img"
          aria-label={chartTitle}
        >
          {chart.ringLevels.map((level) => (
            <circle
              key={level}
              className="eval-compare__radar-ring"
              cx={chart.cx}
              cy={chart.cy}
              r={chart.maxR * level}
            />
          ))}

          {chart.axes.map((axis) => (
            <line
              key={`axis-${axis.questionId}`}
              className="eval-compare__radar-axis"
              x1={chart.cx}
              y1={chart.cy}
              x2={axis.tip.x}
              y2={axis.tip.y}
            />
          ))}

          {chart.othersPts.length >= 3 && (
            <polygon
              className="eval-compare__radar-poly is-others"
              points={pointsToPolygon(chart.othersPts)}
            />
          )}
          {chart.othersPts.length === 2 && (
            <line
              className="eval-compare__radar-poly is-others"
              x1={chart.othersPts[0].x}
              y1={chart.othersPts[0].y}
              x2={chart.othersPts[1].x}
              y2={chart.othersPts[1].y}
            />
          )}
          {chart.selfPts.length >= 3 && (
            <polygon
              className="eval-compare__radar-poly is-self"
              points={pointsToPolygon(chart.selfPts)}
            />
          )}
          {chart.selfPts.length === 2 && (
            <line
              className="eval-compare__radar-poly is-self"
              x1={chart.selfPts[0].x}
              y1={chart.selfPts[0].y}
              x2={chart.selfPts[1].x}
              y2={chart.selfPts[1].y}
            />
          )}

          {chart.axes.map((axis, index) => (
            <g key={`pts-${axis.questionId}`}>
              {axis.othersPoint && (
                <circle
                  className="eval-compare__radar-dot is-others"
                  cx={axis.othersPoint.x}
                  cy={axis.othersPoint.y}
                  r="5"
                >
                  <title>
                    {axis.text}: الآخرون{" "}
                    {Number(axis.othersAvg).toFixed(1)}/{axis.maxScore}
                  </title>
                </circle>
              )}
              {axis.selfPoint && (
                <circle
                  className="eval-compare__radar-dot is-self"
                  cx={axis.selfPoint.x}
                  cy={axis.selfPoint.y}
                  r="5.5"
                >
                  <title>
                    {axis.text}: {selfLabel}{" "}
                    {Number(axis.selfScore).toFixed(1)}/{axis.maxScore}
                  </title>
                </circle>
              )}
              <text
                className="eval-compare__radar-label"
                x={axis.labelPos.x}
                y={axis.labelPos.y}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                <title>{axis.text}</title>
                {index + 1}
              </text>
            </g>
          ))}

          <circle
            className="eval-compare__radar-center"
            cx={chart.cx}
            cy={chart.cy}
            r="3"
          />
        </svg>

        <div className="eval-compare__radar-legend">
          <span className="is-self">● {selfLabel}</span>
          <span className="is-others">● متوسط الآخرين</span>
        </div>

        <ol className="eval-compare__axis-key">
          {chart.axes.map((axis, index) => (
            <li key={`key-${axis.questionId}`}>
              <span className="eval-compare__axis-num">{index + 1}</span>
              <span className="eval-compare__axis-text">{axis.text}</span>
            </li>
          ))}
        </ol>
      </div>

      <ul className="eval-compare__list">
        {comparison.map((row) => {
          const hint = deltaLabel(row.delta, perspective);
          const max = Number(row.maxScore) || 5;
          return (
            <li key={row.questionId}>
              <div className="eval-compare__q">
                <span>{row.text}</span>
                <span className="text-muted small">
                  وزن {Number(row.weightPercent)}%
                </span>
              </div>
              <div className="eval-compare__scores">
                <span className="is-self">
                  {selfLabel}:{" "}
                  {row.selfScore != null
                    ? `${Number(row.selfScore).toFixed(1)} / ${max}`
                    : "—"}
                </span>
                <span className="is-others">
                  الآخرون:{" "}
                  {row.othersAvg != null
                    ? `${Number(row.othersAvg).toFixed(1)} / ${max}`
                    : "—"}
                </span>
              </div>
              {hint && (
                <div className={`eval-compare__delta is-${hint.tone}`}>
                  {hint.text}
                  {row.delta != null && Math.abs(row.delta) >= 0.05
                    ? ` (${row.delta > 0 ? "+" : ""}${Number(row.delta).toFixed(1)})`
                    : ""}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
